/**
 * Comment typing-indicator hooks.
 *
 * Comment threads + replies + reactions now live in the workspace Y.Doc
 * (see `use-canvas-comments.ts`). The only thing that rides AWARENESS is
 * the ephemeral "user is typing in thread X" indicator — kept here on
 * the reserved `comment.*` namespace over the room exposed by
 * `useCanvasYjs()`.
 */

'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCanvasYjs } from '@/components/workspace/canvas/yjs-mount';

const COMMENT_AWARENESS_KEY = 'comment' as const;
const TYPING_STALE_AFTER_MS = 6_000;

interface CommentAwareness {
  typingThreadId?: string;
  typingAt?: number;
}

interface AwarenessState {
  user?: { userId: string; username: string; color: string; peerId: string };
  comment?: CommentAwareness;
}

export interface TypingPeer {
  username: string;
  color: string;
  peerId: string;
  threadId: string;
}

/**
 * Publish that the local user is typing in `threadId` (or null when
 * they stop). Other peers see this via `useTypingPeers(threadId)`.
 */
export function useTypingPublisher(): (threadId: string | null) => void {
  const { workspaceRoom } = useCanvasYjs();
  return useCallback(
    (threadId: string | null) => {
      const awareness = workspaceRoom.awareness;
      if (!awareness) return;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const comment: CommentAwareness = { ...(current.comment ?? {}) };
      if (threadId) {
        comment.typingThreadId = threadId;
        comment.typingAt = Date.now();
      } else {
        delete comment.typingThreadId;
        delete comment.typingAt;
      }
      awareness.setLocalStateField(COMMENT_AWARENESS_KEY, comment);
    },
    [workspaceRoom.awareness],
  );
}

/** Subscribe to other peers' typing indicators for a given thread. */
export function useTypingPeers(threadId: string | null): TypingPeer[] {
  const { workspaceRoom } = useCanvasYjs();
  const [peers, setPeers] = useState<TypingPeer[]>([]);

  useEffect(() => {
    const awareness = workspaceRoom.awareness;
    if (!awareness || !threadId) {
      setPeers([]);
      return;
    }
    const localId = awareness.clientID;
    const compute = () => {
      const now = Date.now();
      const out: TypingPeer[] = [];
      awareness.getStates().forEach((stateRaw, clientId) => {
        if (clientId === localId) return;
        const state = stateRaw as AwarenessState;
        if (!state.user || !state.comment) return;
        if (state.comment.typingThreadId !== threadId) return;
        if (
          !state.comment.typingAt ||
          now - state.comment.typingAt > TYPING_STALE_AFTER_MS
        )
          return;
        out.push({
          username: state.user.username,
          color: state.user.color,
          peerId: state.user.peerId,
          threadId,
        });
      });
      setPeers(out);
    };
    awareness.on('change', compute);
    compute();
    return () => awareness.off('change', compute);
  }, [workspaceRoom.awareness, threadId]);

  return useMemo(() => peers, [peers]);
}
